// The normative schema for ICD-S07 Source Adapter.
//
// Every external source plugs in here, and no adapter can corrupt identity or
// bypass licensing. The three rules that shape this file:
//
//   1. An adapter never mints an entity id. Everything it emits is keyed by a
//      vendor `(namespace, key)` pair; the registry resolves them.
//   2. An adapter never writes. `extract` returns values; the platform stores.
//   3. Rights attach to a RESOURCE, not to a source. nflverse publishes its own
//      CC BY 4.0 data alongside Pro-Football-Reference scrapes, OverTheCap
//      contracts and CC BY-SA charting through identical plumbing — a
//      source-level license would wave four rights classes through unexamined.
//
// A manifest is DATA, not code: it round-trips to the `source_resource` table
// and back, so nothing here may be a function.

import { z } from 'zod';

const Iso = z.string().regex(/^\d{4}-\d{2}-\d{2}/, 'ISO 8601 date or timestamp');

/**
 * How a resource is fetched. Bulk is two shapes rather than one: `pbp` carries
 * 164 assets and `play_by_play_1999.csv` has not changed since April 2025, so
 * treating it as a single resource means re-fetching 2.4 GB to learn that one
 * season moved.
 */
export const Acquisition = z.enum([
  'bulk-whole',
  'bulk-partitioned',
  'rest-page',
  'entity-fanout',
  'feed',
  'query',
  'synthetic',
]);
export type Acquisition = z.infer<typeof Acquisition>;

/**
 * How the data behaves in time. A separate axis from acquisition, because they
 * compose: NWS is one source whose `/points` lookup is effectively static and
 * whose forecast refreshes hourly, so a TTL belongs to the stage.
 */
export const Freshness = z.enum([
  'static',
  'daily-rebuild',
  'continuous',
  'event-driven',
  'forecast-window',
]);
export type Freshness = z.infer<typeof Freshness>;

/**
 * What a source allows us to spend.
 *
 * A single `rateLimit` number cannot express this: four of the candidate
 * sources use mutually incompatible units, and Wikidata's is query-seconds per
 * minute with a parallelism cap. The runner enforces the budget; adapters never
 * self-police, because a per-adapter budget cannot see what the others spend.
 */
export const Budget = z.discriminatedUnion('unit', [
  z.object({ unit: z.literal('requests-per-minute'), max: z.number().int().positive() }),
  z.object({ unit: z.literal('requests-per-day'), max: z.number().int().positive() }),
  z.object({
    unit: z.literal('credits-per-month'),
    max: z.number().int().positive(),
    // Not a function: a manifest has to survive a round trip through the
    // database, and a callback does not.
    weightPerItem: z.number().positive(),
  }),
  z.object({
    unit: z.literal('query-seconds-per-minute'),
    max: z.number().positive(),
    parallelism: z.number().int().positive(),
  }),
  z.object({ unit: z.literal('bytes-per-month'), max: z.number().int().positive() }),
  // No published limit. Saying so is the honest option, and it is not the same
  // as claiming there is no limit.
  z.object({ unit: z.literal('courtesy') }),
]);
export type Budget = z.infer<typeof Budget>;

/**
 * One resource, and the rights that come with it.
 *
 * `commercialUse` is the gate. `lint:sources` reads it, and the database
 * refuses `enabled = 1` without it.
 */
export const ResourceManifest = z.object({
  /** Logical name: 'schedules/games', 'injuries/2026'. */
  resource: z.string().min(1),
  acquisition: Acquisition,
  freshness: Freshness,

  /** SPDX identifier where one exists; a plain description where none does. */
  license: z.string().min(1),
  licenseUrl: z.string().url(),
  /** The gate. False means this resource may never be enabled. */
  commercialUse: z.boolean(),
  /** Whether the obligation travels to anything derived from it. */
  shareAlike: z.boolean(),
  /** May we serve these rows onward, or only derive from them? */
  redistribution: z.boolean(),

  /** Who actually owns the data, when that is not the host we fetch from. */
  upstreamName: z.string().nullable(),
  upstreamUrl: z.string().url().nullable(),

  /** Rendered verbatim on About and the admin dashboard. Null = none required. */
  attributionText: z.string().nullable(),
  termsUrl: z.string().url().nullable(),
  /** When a human last read the terms. Ages into a gate warning at twelve months. */
  termsReadAt: Iso,
});
export type ResourceManifest = z.infer<typeof ResourceManifest>;

/** One source, its resources, and what we may spend against it. */
export const SourceManifest = z.object({
  id: z.string().regex(/^[a-z][a-z0-9-]*$/, 'lowercase kebab-case'),
  name: z.string().min(1),
  homepage: z.string().url(),
  /** Lower wins when two sources disagree about the same fact. */
  trustRank: z.number().int().min(0).max(100),
  kind: z.enum(['provider', 'derived', 'human']),
  resources: z.array(ResourceManifest).min(1),
  budget: Budget,
  /** Cron expression, or 'manual'. */
  cadence: z.string().min(1),
  /**
   * Further triggers that run a narrower plan: nflverse's game-day checks.
   * The plan is told which trigger fired (`PlanContext.cron`) and decides
   * what that trigger is for. The registry keeps `cadence` alone, so the
   * source's recorded schedule does not flip between the two.
   */
  extraCadences: z.array(z.string().min(1)).optional(),
  /** Fact kinds this source may emit. An undeclared kind fails conformance. */
  emits: z.array(z.string()).min(1),
  /** Entity types this source may claim exist. */
  asserts: z.array(z.enum(['person', 'team', 'venue', 'contest', 'org'])),
  /** Routes every fact produced. Null where genuinely sport-neutral. */
  sport: z.string().nullable(),
  league: z.string().nullable(),
});
export type SourceManifest = z.infer<typeof SourceManifest>;

/** A thing the source asserts exists, keyed the way the source keys it. */
export interface CandidateEntity {
  type: 'person' | 'team' | 'venue' | 'contest' | 'org';
  namespace: string;
  key: string;
  display: string;
  /** Other vendors' keys this record carries, for crosswalk resolution. */
  otherKeys: Record<string, string>;
  /** Evidence for attribute matching when no key resolves. Never a name alone. */
  evidence: Record<string, string | null>;
}

/** A reference from a fact to an entity, by vendor key, with its role. */
export interface EntityRefCandidate {
  namespace: string;
  key: string;
  role: string;
}

/** One assertion, before identity resolution. */
export interface CandidateFact {
  kind: string;
  /**
   * What this fact is ABOUT, in the source's own terms — its grain.
   *
   * Two extractions that produce the same `(kind, key)` are talking about the
   * same thing, so the second one either changes it or is a no-op. Without
   * this, a daily-rebuild source has no way to say so: nflverse republishes
   * players.csv every morning, and one edited row changes the bytes, which
   * changes the content hash, which re-extracts all 24,820 records. Appending
   * them would write nine million rows a year to say almost nothing.
   *
   * The adapter supplies it because only the adapter knows the grain —
   * `game_id` for a contest, `gsis|season|week` for an injury report. It must
   * be unique within a `kind` across one extraction; conformance check 16
   * enforces that, because a duplicated key silently supersedes a sibling.
   */
  key: string;
  payload: unknown;
  validFrom: string;
  /** Null means still true — distinct from unknown, always. */
  validTo: string | null;
  refs: EntityRefCandidate[];
  topics: string[];
  confidence: 'official' | 'reported' | 'derived' | 'estimated';
  obsStatus?: 'live' | 'provisional' | 'final' | 'revised';
}

/** Vocabulary we did not recognize. Raised, never dropped. */
export interface UnmappedValue {
  field: string;
  value: string;
  count: number;
  /** One source key, so it can be looked up. */
  sample: string;
}

/**
 * A slice of the world this extraction saw ALL of.
 *
 * Absence only carries information when the source was complete over something.
 * nflverse's injuries file for a week lists everyone on that week's report, so
 * a player who was Questionable on Wednesday and is gone on Thursday is no
 * longer designated — but nothing in the file says so, and without this the
 * Wednesday fact simply stays true forever. That is the "leaves players
 * Questionable forever" failure ICD-P01 §6.3 names.
 *
 * The prefix is matched against `CandidateFact.key`, which is why the weekly
 * kinds order their key `season|period|person` rather than person-first.
 */
export interface CompleteScope {
  kind: string;
  /** Natural-key prefix, e.g. `2026|1|`. Empty means the whole kind. */
  prefix: string;
}

/** What `extract` returns. Values only — nothing here has been written. */
export interface ExtractResult {
  entities: CandidateEntity[];
  facts: CandidateFact[];
  unmapped: UnmappedValue[];
  /**
   * Optional, and absent means "this file says nothing about what is missing
   * from it" — the safe reading, and the right one for a directory: a person
   * filtered out of players.csv has not stopped existing.
   */
  complete?: CompleteScope[];
  /**
   * Records deliberately skipped, with the reason. Together with `facts` and
   * `unmapped` these must account for every input record: conformance test 9
   * reconciles the counts exactly, so nothing can vanish quietly.
   */
  skipped: { reason: string; count: number }[];
}

export interface FetchItem {
  resource: string;
  url: string;
  /** For a partitioned resource: which partition. */
  partition?: string;
  /**
   * Fetch at most this many bytes from the START of the asset.
   *
   * For an append-at-the-top snapshot file, where the newest rows are first and
   * everything below them is history we already hold. nflverse's depth charts
   * are 46.8 MB by September and growing — 177 snapshots since March, of which
   * exactly one answers "is he the starter" — and the newest occupies the first
   * 213 KB. Fetching the rest costs a Worker's whole memory budget, a fresh
   * 46.8 MB capture every day, and more of both as the season runs on.
   *
   * THE CAPTURE IS THEN A PREFIX, NOT THE FILE, and an adapter that sets this
   * owes the difference: it must prove from the bytes it got that it saw a
   * whole unit of whatever it came for, and fail loudly when it did not. A
   * prefix that silently contains half a snapshot is worse than no snapshot.
   */
  maxBytes?: number;
}

export interface FetchPlan {
  items: FetchItem[];
  /** Why this plan, in one line, for the run ledger. */
  because: string;
}

export type ProbeResult =
  | { changed: false; token: string }
  | { changed: true; token: string; bytes?: number }
  /** No cheap probe exists. Fetch and compare hashes. Not a failure. */
  | { changed: 'unknown' };

export interface StoredCapture {
  id: string;
  resource: string;
  fetchedAt: string;
  /**
   * Which partition these bytes are, when the resource has them.
   *
   * A bulk file is self-describing: every row of `schedules/games.csv` says
   * which game it is. A fanned-out capture is not — one venue's forecast is
   * 164 KB of hourly periods that never names the stadium it is for, so
   * without this `extract` cannot key its facts or reference the venue. It is
   * carried from `FetchItem.partition` verbatim.
   */
  partition?: string;
  /** The bytes, verbatim, exactly as the source sent them. */
  body: Uint8Array;
}

/**
 * One thing to go and get, for a source that fans out over our own entities.
 *
 * **The platform builds this list, never the adapter** — the same rule that
 * keeps an adapter from minting an id or writing a row. An adapter that could
 * query for its own work list would be an adapter that could reach the
 * database, and the purity of `plan()` is what makes fixture replay a real
 * test.
 *
 * It is keyed by vendor namespace, not by our entity ids, for the same reason
 * everything else in this file is: an adapter never learns our ids.
 */
export interface WorkItem {
  /** What the work is about — a venue, a contest — in the source registry's terms. */
  namespace: string;
  key: string;
  display: string;
  /**
   * When this work stops being worth doing. A kickoff, a deadline. The planner
   * uses it to leave alone what is too far out to forecast.
   */
  dueAt: string | null;
  /**
   * Everything else the planner knows and the adapter needs: coordinates, the
   * contest this venue is hosting, a cached grid cell. Strings and numbers
   * only, because a work list has to survive being logged.
   */
  attrs: Record<string, string | number | boolean | null>;
}

export interface PlanContext {
  now: string;
  /**
   * The trigger that started this run: the manifest's `cadence`, one of its
   * `extraCadences`, or null for a manual run (which plans everything).
   */
  cron?: string | null;
  /** Per-resource watermarks: the last good probe token and count. */
  watermarks: Record<string, { probeToken: string | null; lastGoodCount: number | null }>;
  /**
   * The work list, for `entity-fanout`. Empty for every other acquisition
   * shape, and empty is not an error — a week with no upcoming fixtures has
   * nothing to forecast, and the honest plan is no items at all.
   */
  work?: readonly WorkItem[];
}

export interface FetchContext {
  now: string;
  fetch: typeof fetch;
}

export interface ExtractContext {
  now: string;
  /** Positions, stat keys and injury statuses, from the sport package. */
  vocabulary: Record<string, ReadonlySet<string>>;
}

/** One of our standard terms. Queries name these; sources never do. */
export interface Concept {
  id: string;
  domain: string;
  sport: string | null;
  display: string;
}

/** One source's word, and the concept it carries. */
export interface ConceptMapping {
  domain: string;
  sourceValue: string;
  conceptId: string;
}

/**
 * The coded words a source speaks, and what we take them to mean.
 *
 * Optional: a source with no coded values declares none. Where it exists the
 * runner writes it before extracting, which is what turns the unmapped report
 * from a daily wall of known vocabulary into the one line that matters — the
 * day a source adds a word nobody has accounted for.
 */
export interface SourceVocabulary {
  concepts: readonly Concept[];
  mappings: readonly ConceptMapping[];
}

/**
 * The contract every source implements.
 *
 * `plan` and `extract` are pure — same inputs, same outputs, forever — which is
 * what makes fixture replay a real test rather than a ceremony.
 */
export interface SourceAdapter {
  readonly manifest: SourceManifest;
  /** Coded values this source uses. See `SourceVocabulary`. */
  readonly vocabulary?: SourceVocabulary;
  /**
   * When a game's stat lines are due in our database, from its kickoff: the
   * source's publishing schedule and our reads of it (ICD-S07 §4). Optional:
   * only a source that writes stat lines on a schedule has one, and the late
   * lines alarm reads it (DIRECTION D11). Pure. Null when no line is due —
   * out of season, for one.
   */
  linesDueBy?(kickoff: string): string | null;
  plan(ctx: PlanContext): FetchPlan;
  probe(item: FetchItem, ctx: FetchContext): Promise<ProbeResult>;
  fetch(item: FetchItem, ctx: FetchContext): Promise<StoredCapture>;
  extract(capture: StoredCapture, ctx: ExtractContext): ExtractResult;
}
